import React, { forwardRef, useState, useId } from 'react';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'autoComplete'> {
    /** Label text */
    label?: string;
    /** Label extra className */
    labelClassName?: string;
    /** Helper / hint text */
    helperText?: string;
    /** Error message */
    error?: string;
    /** Size variant */
    size?: 'small' | 'medium' | 'large';
    /** Left icon / addon */
    leftIcon?: React.ReactNode;
    /** Right icon / addon */
    rightIcon?: React.ReactNode;
    /** Full width */
    fullWidth?: boolean;
    /** Required indicator */
    required?: boolean;
    /** Auto-complete toggle */
    autoComplete?: React.HTMLInputAutoCompleteAttribute | boolean;
}

const sizeClasses = {
    small: 'text-[13px] py-2 px-3 min-h-[36px]',
    medium: 'text-[15px] py-2.5 px-3.5 min-h-[42px]',
    large: 'text-base py-3 px-4 min-h-[48px]',
};

const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
    {
        label,
        labelClassName = '',
        helperText,
        error,
        size = 'medium',
        leftIcon,
        rightIcon,
        fullWidth = true,
        disabled,
        required,
        style,
        id: externalId,
        className = '',
        autoComplete,
        ...props
    },
    ref
) {
    const autoId = useId();
    const inputId = externalId || autoId;
    const hasError = Boolean(error);
    const [isFocused, setIsFocused] = useState(false);

    const autoCompleteValue =
        typeof autoComplete === 'boolean'
            ? autoComplete ? 'on' : 'off'
            : autoComplete;

    return (
        <div className={fullWidth ? 'w-full' : ''}>
            {/* Label */}
            {label && (
                <label
                    htmlFor={inputId}
                    className={`block text-[15px] font-semibold text-[var(--v2-text-primary)] mb-1.5 tracking-[-0.005em] ${labelClassName}`}
                >
                    {label} {required && <span className="text-[var(--v2-danger)]">*</span>}
                </label>
            )}

            {/* Input wrapper */}
            <div
                className={`relative ${fullWidth ? 'block' : 'inline-block'} group`}
                style={fullWidth ? { width: '100%' } : undefined}
            >
                {/* Left icon */}
                {leftIcon && (
                    <span
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-[var(--v2-text-muted)] pointer-events-none flex items-center justify-center"
                    >
                        {leftIcon}
                    </span>
                )}

                <input
                    ref={ref}
                    id={inputId}
                    disabled={disabled}
                    required={required}
                    autoComplete={autoCompleteValue}
                    aria-invalid={hasError || undefined}
                    aria-describedby={
                        (error ? `${inputId}-error` : '') + (helperText ? ` ${inputId}-helper` : '') || undefined
                    }
                    className={[
                        // Base
                        'w-full font-[inherit] outline-none',
                        'rounded-[var(--radius-md)]',
                        'border transition-all',
                        'duration-[var(--duration-normal)] ease-[var(--ease-calm)]',

                        // Size
                        sizeClasses[size],

                        // Icon padding
                        leftIcon ? 'pl-10' : '',
                        rightIcon ? 'pr-10' : '',

                        // Colors — error vs normal
                        hasError
                            ? 'border-[var(--v2-danger)]/60 text-[var(--v2-text-primary)] placeholder-[var(--v2-danger)]/40'
                            : [
                                'border-[var(--v2-border-glass-strong)]',
                                'text-[var(--v2-text-primary)]',
                                'placeholder-[var(--v2-text-muted)]',
                                'hover:border-[var(--v2-primary-400)]/50',
                            ].join(' '),

                        // Background
                        isFocused
                            ? 'bg-[var(--v2-bg-surface)]'
                            : 'bg-[var(--v2-bg-card)]',

                        // Focus ring
                        hasError
                            ? 'focus:ring-[3px] focus:ring-[var(--v2-danger)]/15 focus:border-[var(--v2-danger)]'
                            : 'focus:ring-[3px] focus:ring-[var(--v2-primary-500)]/18 focus:border-[var(--v2-primary-500)]',

                        // Disabled
                        'disabled:opacity-50 disabled:cursor-not-allowed',

                        // Shadow
                        'shadow-[0_1px_2px_rgba(0,0,0,0.04),inset_0_1px_0_rgba(255,255,255,0.015)]',

                        className,
                    ].filter(Boolean).join(' ')}
                    style={style}
                    onFocus={(e) => {
                        setIsFocused(true);
                        props.onFocus?.(e);
                    }}
                    onBlur={(e) => {
                        setIsFocused(false);
                        props.onBlur?.(e);
                    }}
                    {...props}
                />

                {/* Right icon */}
                {rightIcon && (
                    <span
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-[var(--v2-text-muted)] pointer-events-none flex items-center justify-center"
                    >
                        {rightIcon}
                    </span>
                )}
            </div>

            {/* Error */}
            {error && (
                <p
                    id={`${inputId}-error`}
                    role="alert"
                    className="text-[length:var(--font-caption,12px)] text-[var(--v2-danger)] mt-1 flex items-center gap-1.5 font-medium"
                >
                    {error}
                </p>
            )}

            {/* Helper text */}
            {helperText && !error && (
                <p
                    id={`${inputId}-helper`}
                    className="text-[length:var(--font-caption,12px)] text-[var(--v2-text-muted)] mt-1"
                >
                    {helperText}
                </p>
            )}
        </div>
    );
});

export default Input;
